import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, ShieldCheck, FileText, Lock, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkspacePortal() {
  const { token } = useParams();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(null);
  const [question, setQuestion] = useState("");
  const [doc, setDoc] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/workspace-portal/${token}`)
      .then(({ data }) => setRoom(data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid or has been revoked."));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="min-h-screen bg-navy flex items-center justify-center text-center px-6">
      <div><Lock className="w-10 h-10 text-white/30 mx-auto" /><p className="text-white/70 mt-4 text-sm" data-testid="portal-error">{String(error)}</p></div>
    </div>
  );
  if (!room) return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="w-7 h-7 text-cobalt animate-spin" /></div>;

  const acceptNda = async () => {
    try { await axios.post(`${API}/workspace-portal/${token}/accept-nda`); toast.success("NDA accepted — recorded with timestamp and IP"); load(); }
    catch { toast.error("Failed"); }
  };
  const openDoc = async (id) => {
    try {
      const r = await axios.get(`${API}/workspace-portal/${token}/docs/${id}`, { responseType: "text" });
      setDoc({ id, content: r.data });
    } catch (e) { toast.error(String(e.response?.data?.detail?.message || e.response?.data?.detail || "Access denied")); }
  };
  const ask = async () => {
    if (!question.trim()) return;
    try { await axios.post(`${API}/workspace-portal/${token}/questions`, { text: question }); toast.success("Question sent to the Crash Ready security team"); setQuestion(""); load(); }
    catch (e) { toast.error(String(e.response?.data?.detail || "Failed")); }
  };

  const ndaGate = room.nda.required && !room.nda.accepted;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="workspace-portal">
      <header className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="text-[10px] font-bold uppercase tracking-widest text-cobalt">Crash Ready · Secure Due Diligence Room</div>
          <h1 className="font-heading text-xl font-extrabold mt-1" data-testid="portal-org">{room.workspace.name}</h1>
          <p className="text-white/50 text-xs mt-1">Welcome, {room.member.name} ({room.member.role.replace("_", " ")}). {room.note}</p>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-5">
        {ndaGate ? (
          <div className="rounded-3xl bg-white border border-slate-100 p-8 text-center" data-testid="portal-nda-gate">
            <Lock className="w-8 h-8 text-cobalt mx-auto" />
            <h2 className="font-heading font-bold text-navy mt-3">Non-disclosure agreement required</h2>
            <p className="text-xs text-slate-500 mt-3 max-w-2xl mx-auto text-left rounded-xl bg-slate-50 p-4">{room.nda.text}</p>
            <button onClick={acceptNda} data-testid="portal-accept-nda" className="mt-4 px-6 py-2.5 rounded-full bg-navy text-white text-sm font-bold">I accept the NDA</button>
            <p className="text-[10px] text-slate-400 mt-2">Your acceptance is recorded electronically with timestamp and IP address.</p>
          </div>
        ) : (
          <>
            <div className="rounded-3xl bg-white border border-slate-100 p-6" data-testid="portal-posture">
              <h2 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cobalt" /> Security posture (live, honest)</h2>
              <p className="text-xs text-slate-500 mt-2">{room.posture.statement}</p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                {[["Controls", room.posture.controls.total], ["Operating w/ evidence", room.posture.controls.operating_with_evidence], ["Implemented", room.posture.controls.implemented]].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-slate-50 p-3"><div className="font-heading font-extrabold text-navy text-lg">{v}</div><div className="text-[9px] uppercase font-bold text-slate-400">{l}</div></div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">SOC 2 program: {room.posture.soc2_program_status} · HIPAA: {room.posture.hipaa_posture}</p>
              {room.posture.certifications?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">{room.posture.certifications.map((c) => <span key={c.cert_id} className="px-2.5 py-1 rounded-full bg-cobalt/10 text-cobalt text-[10px] font-bold">{c.name}: {c.status}</span>)}</div>
              )}
            </div>
            <div className="rounded-3xl bg-white border border-slate-100 p-6" data-testid="portal-docs">
              <h2 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-cobalt" /> Shared documents ({room.documents.length})</h2>
              <div className="mt-3 space-y-1.5">
                {room.documents.map((d) => (
                  <button key={d.doc_id} onClick={() => openDoc(d.doc_id)} data-testid={`portal-doc-${d.doc_id}`}
                    className="w-full text-left flex justify-between rounded-xl border border-slate-100 px-4 py-2.5 text-xs hover:border-cobalt transition-colors">
                    <span className="text-slate-600 font-semibold">{d.title}</span>
                    <span className="text-slate-400">{d.doc_type.replace(/_/g, " ")} · v{d.version} · {d.classification}</span>
                  </button>
                ))}
                {!room.documents.length && <p className="text-[11px] text-slate-300">No documents shared yet.</p>}
              </div>
              {doc && (
                <pre className="mt-3 rounded-xl bg-navy text-white/80 p-4 text-[10px] max-h-72 overflow-auto whitespace-pre-wrap" data-testid="portal-doc-view">{doc.content}</pre>
              )}
            </div>
            <div className="rounded-3xl bg-white border border-slate-100 p-6" data-testid="portal-qa">
              <h2 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cobalt" /> Questions for the security team</h2>
              {room.member.role !== "viewer" && (
                <div className="mt-3 flex gap-2">
                  <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask about security, compliance or data handling…"
                    data-testid="portal-question-input" className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30" />
                  <button onClick={ask} data-testid="portal-ask" className="px-5 py-2 rounded-full bg-navy text-white text-xs font-bold">Send</button>
                </div>
              )}
              <div className="mt-3 space-y-1.5">
                {room.questions.map((q) => (
                  <div key={q.question_id} className="rounded-xl bg-slate-50 px-3 py-2 text-xs">
                    <b className="text-navy">{q.asked_by}:</b> <span className="text-slate-600">{q.text}</span>
                    {q.answer && <div className="text-emerald-700 mt-1">Crash Ready: {q.answer}</div>}
                    {!q.answer && <div className="text-[10px] text-slate-400 mt-0.5">Awaiting response…</div>}
                  </div>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-slate-400 text-center pb-4">{room.posture.disclaimer}</p>
          </>
        )}
      </div>
    </div>
  );
}
